import React from 'react';
import Avatar from '@atlaskit/avatar';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import Lozenge from '@atlaskit/lozenge';
import WarningIcon from '@atlaskit/icon/core/warning';
import { splitAndCapitalize, isAchPaymentMethod } from '@css-xp/ppc-utils';
import { CustomerContactsAvatarGroup } from '@css-xp/ppc-subscription-table/src/components/customer-contacts-avatar-group';
import { PaymentMethodPanel, PaymentMethodId } from '@atlassian/commerce-payment-methods';
// @ts-ignore
import TermPaymentIcon from '@css-xp/base/assets/img/icon-term-payment.svg';
import {
  PaymentMethodType,
  PaymentMethod,
  CreditCard,
  PaymentOnTerms,
  InvoiceGroup,
  ContactInfo,
  PaymentMethodTypeV2,
  Ach,
} from '@css-xp/graphtypes/src/generated-types-new';
// @ts-ignore
import UserIcon from '@css-xp/base/assets/img/application/icon-user.svg';
// @ts-ignore
import PaypalIcon from '@css-xp/base/assets/img/paypal-icon.svg';
import { AchPanel } from '@css-xp/base';
import { getDisableReason } from '../../../order-summary/src/components/change-billing-profile';
import {
  container,
  billingProfileContentContainer,
  paymentMethodContentContainer,
  billingProfileShipToDetailsContainer,
  primaryContactContainer,
  StyledPaymentMethodPanel,
  DisabledWarnigIcon,
  StyledBillingProfileName,
  StyledVerticalDivider,
  disabledPrimaryContactContainer,
  disabledPaymentMethodContentContainer,
} from './billing-profile-card.styles';

export interface BillingProfileCardProps {
  billingProfile: InvoiceGroup;
  primaryCustomerContact: ContactInfo;
  isInEligible?: boolean;
  onSelectBillingProfile?: (_billingProfileId: string) => void;
  shouldShowACHPaymentMethod?: boolean;
}

export const BillingProfileCard = ({
  billingProfile,
  primaryCustomerContact,
  isInEligible = false,
  onSelectBillingProfile,
  shouldShowACHPaymentMethod = false,
}: BillingProfileCardProps) => {
  const getDefaultPaymentSection = (defaultPaymentMethod?: PaymentMethod | null) => {
    if (!defaultPaymentMethod) {
      return (
        <Flex gap="space.150" alignItems="center" justifyContent="start">
          <DisabledWarnigIcon>
            <WarningIcon label="" />
          </DisabledWarnigIcon>
          <Text color="color.text.subtlest">No payment method</Text>
        </Flex>
      );
    }

    if (defaultPaymentMethod?.type === PaymentMethodType?.CARD) {
      const defaultPaymentMethodObj = defaultPaymentMethod as CreditCard;
      const expMonth = +(defaultPaymentMethodObj?.card?.expirationMonth ?? 0);
      const expYear = +(defaultPaymentMethodObj?.card?.expirationYear ?? 0);
      const card = {
        brand: defaultPaymentMethodObj?.card?.brand ?? '',
        expMonth,
        expYear,
        last4: defaultPaymentMethodObj?.card?.last4Numbers ?? '',
        name: defaultPaymentMethodObj?.card?.cardHolderName ?? '',
      };

      return (
        <Box padding="space.050" testId="ppc-payment-methods-line-item">
          <StyledPaymentMethodPanel>
            <PaymentMethodPanel
              paymentMethod={{
                id: defaultPaymentMethodObj.id as PaymentMethodId,
                type: defaultPaymentMethodObj.type,
                //@ts-ignore
                card,
                default: true,
              }}
            />
          </StyledPaymentMethodPanel>
        </Box>
      );
    }
    // @ts-ignore
    if (defaultPaymentMethod?.type === PaymentMethodType?.DEFERRED) {
      const defaultPaymentMethodObj = defaultPaymentMethod as PaymentOnTerms;
      const hasPayByStatement = defaultPaymentMethodObj?.terms?.payOnStatement;
      const invoiceDueDays = defaultPaymentMethodObj?.terms?.invoiceDueDays;
      const termPaymentType = invoiceDueDays && `NET${invoiceDueDays}`;

      return (
        <Flex alignItems="center" gap="space.200" testId="term-payment-method-v2">
          {/* @ts-ignore */}
          <img src={TermPaymentIcon} height="16" width="24" />
          <Text color="color.text.subtle" testId="term-payment-type">
            {hasPayByStatement ? 'On-account' : `${termPaymentType}`}
          </Text>
        </Flex>
      );
    }

    // @ts-ignore
    if (defaultPaymentMethod?.type === PaymentMethodTypeV2.PAYPAL) {
      return (
        <Flex alignItems="center">
          {/* @ts-ignore */}
          <img src={PaypalIcon} height="16" width="24" />
          <Text color="color.text.subtle" testId="term-payment-type" weight="bold">
            PayPal
          </Text>
        </Flex>
      );
    }

    if (shouldShowACHPaymentMethod && isAchPaymentMethod(defaultPaymentMethod?.type)) {
      const defaultPaymentMethodObj = defaultPaymentMethod as Ach;
      return <AchPanel bankName={defaultPaymentMethodObj?.ach?.bankName} last4={defaultPaymentMethodObj?.ach?.last4} />;
    }
  };

  const handleOnSelectBillingProfile = () => {
    if (isInEligible) return;
    if (onSelectBillingProfile && billingProfile?.id) {
      onSelectBillingProfile(billingProfile?.id);
    }
  };

  return (
    <Box
      padding="space.200"
      xcss={container(!isInEligible)}
      as="button"
      style={{ cursor: isInEligible ? 'not-allowed' : 'pointer', marginLeft: '0px' }}
      onClick={handleOnSelectBillingProfile}
    >
      <Stack>
        <Flex alignItems="center" gap="space.150" justifyContent="space-between">
          <Box xcss={billingProfileContentContainer}>
            <Stack space="space.050" alignInline="start">
              <Flex gap="space.100">
                <StyledBillingProfileName isInEligible={isInEligible}>{billingProfile?.name}</StyledBillingProfileName>
                {isInEligible && <Lozenge appearance="removed">Not available</Lozenge>}
              </Flex>
              <Flex alignItems="center">
                <Text color={isInEligible ? 'color.text.disabled' : 'color.text.subtlest'} size="small">
                  {splitAndCapitalize(billingProfile?.shipToPriceEligibility)}
                </Text>
                {billingProfile?.currency && (
                  <>
                    <StyledVerticalDivider isInEligible={isInEligible} />
                    <Text color={isInEligible ? 'color.text.disabled' : 'color.text.subtlest'} size="small">
                      {billingProfile?.currency}
                    </Text>
                  </>
                )}
                {billingProfile?.invoiceGroupNumber && (
                  <>
                    <StyledVerticalDivider isInEligible={isInEligible} />
                    <Flex>
                      <Text
                        color={isInEligible ? 'color.text.disabled' : 'color.text.subtlest'}
                        size="small"
                        align="start"
                      >
                        {billingProfile?.invoiceGroupNumber}
                      </Text>
                    </Flex>
                  </>
                )}
              </Flex>
            </Stack>
          </Box>
          <Stack
            xcss={isInEligible ? disabledPaymentMethodContentContainer : paymentMethodContentContainer}
            alignInline="start"
          >
            {getDefaultPaymentSection(billingProfile?.defaultPaymentMethod)}
          </Stack>
          <Stack xcss={isInEligible ? disabledPrimaryContactContainer : primaryContactContainer} alignInline="start">
            {primaryCustomerContact ? (
              <CustomerContactsAvatarGroup
                contacts={[primaryCustomerContact] as any}
                avatarSize="small"
                showContactType={true}
              />
            ) : (
              <Tooltip content="Unassigned (Primary)">
                <Flex justifyContent="start">
                  <Avatar src={UserIcon.toString()} testId="unassigned-primary-contact" size="small" />
                </Flex>
              </Tooltip>
            )}
          </Stack>
          <Stack xcss={billingProfileShipToDetailsContainer}>
            <Text align="start" color={isInEligible ? 'color.text.disabled' : 'color.text'}>
              {billingProfile?.shipToParty?.name}, {billingProfile?.shipToParty?.addressLine1},{' '}
              {billingProfile?.shipToParty?.city}, {billingProfile?.shipToParty?.postcode}{' '}
              {billingProfile?.shipToParty?.state}, {billingProfile?.shipToParty?.country}
            </Text>
          </Stack>
        </Flex>
      </Stack>
      <Stack>
        <Flex>
          {isInEligible && (
            <Box paddingBlockStart="space.100">
              <Text color="color.text.danger">{getDisableReason(false, true, false)}</Text>
            </Box>
          )}
        </Flex>
      </Stack>
    </Box>
  );
};
